import { NgModule, ModuleWithProviders } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';

import { AuthGuard } from "app/shared/guards/auth.guard";
import { AvatarModule } from 'primeng/avatar';

import { CommonService } from "app/shared/services/common.service";
import { HttpClient } from "@angular/common/http";
import { APIInterceptor } from './interceptors/api-url.interceptor';
import { AccordionModule } from 'primeng/accordion';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { CarouselModule } from 'primeng/carousel';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipsModule } from 'primeng/chips';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ColorPickerModule } from 'primeng/colorpicker';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DataViewModule } from 'primeng/dataview';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FieldsetModule } from 'primeng/fieldset';
import { FileUploadModule } from 'primeng/fileupload';
import { TagModule } from 'primeng/tag';
import { FormControlDirective } from './form-validations/directives/form-control.directive';
import { FormGroupComponent } from './form-validations/components/form-group/form-group.component';
import { FormValidationDirective } from './form-validations/directives/form-validation.directive';
import { MessagesComponent } from './form-validations/components/messages/messages.component';
import { GalleriaModule } from 'primeng/galleria';
import { InplaceModule } from 'primeng/inplace';
import { InputMaskModule } from 'primeng/inputmask';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ListboxModule } from 'primeng/listbox';
import { MegaMenuModule } from 'primeng/megamenu';
import { MenuModule } from 'primeng/menu';
import { MenubarModule } from 'primeng/menubar';
import { MessagesModule } from 'primeng/messages';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { OrderListModule } from 'primeng/orderlist';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { SidebarModule } from 'primeng/sidebar';
import { PaginatorModule } from 'primeng/paginator';
import { PanelModule } from 'primeng/panel';
import { PanelMenuModule } from 'primeng/panelmenu';
import { PasswordModule } from 'primeng/password';
import { PickListModule } from 'primeng/picklist';
import { ProgressBarModule } from 'primeng/progressbar';
import { RadioButtonModule } from 'primeng/radiobutton';
import { RatingModule } from 'primeng/rating';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SlideMenuModule } from 'primeng/slidemenu';
import { SliderModule } from 'primeng/slider';
import { SpinnerModule } from 'primeng/spinner';
import { SplitButtonModule } from 'primeng/splitbutton';
import { StepsModule } from 'primeng/steps';
import { TabMenuModule } from 'primeng/tabmenu';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TerminalModule } from 'primeng/terminal';
import { TieredMenuModule } from 'primeng/tieredmenu';
import { ToastModule } from 'primeng/toast';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from 'primeng/tree';
import { TreeTableModule } from 'primeng/treetable';
import { BlockUIModule } from 'primeng/blockui';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { VirtualScrollerModule } from 'primeng/virtualscroller';
import { ImageModule } from 'primeng/image';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AppAuthService } from './services/app-auth.service';
import { ProvideParentForm } from './directives/provide-parent-form.directive';
import { AppAutofocus } from './directives/auto-focus.directive';
import { OnlyNumber } from './directives/only-number.directive';
import { AppEqualToValueValidator } from './directives/equal-to-value-validator.directive';
import { AppEqualValidator } from './directives/equal-to-validation.directive';
import { AppNotZeroValidator } from './directives/not-zero-validation.directive';
import { AppUrlValidator } from './directives/url-validation.directive';
import { EnumTranslatePipe } from './localization/enum-translate.pipe';
import { PasswordViewPipe } from './pipes/password-view.pipe';
import { CalendarComponent } from './components/calendar/calendar.component';
import { DelayClickDirective } from './directives/delay-click.directive';
import { MaxValidatorDirective } from './validators/max/max-validator.directive';
import { MinValidatorDirective } from './validators/min/min-validator.directive';
import { ConfirmationService } from 'primeng/api';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { AmountInputComponent } from './components/amount-input/amount-input.component';
import { CustomMessageComponent } from './components/customMessage/customMessage.component';
import { DialogComponent } from './components/dialog/dialog.component';
import { FilterPipe } from './pipes/filter.pipe';
import { AdvMessageService } from './services/message.service';
import { UploadFileComponent } from './components/upload-file/upload-file.component';
import { CodeVerificationComponent } from './components/code-verification/code-verification.component';
import { TelOrMailComponent } from './components/telOrMail/tel-or-mail.component';
import { TelOrMailDirective } from './validators/telOrMail/telOrMail.directive';
import { EnumService } from 'app/core/services/enum.service';
import { AgGridModule } from 'ag-grid-angular';
import { CascadeSelectModule } from 'primeng/cascadeselect';
import { InputNumberModule } from 'primeng/inputnumber';
import { CaptchaComponent } from './components/captcha/captcha.component';
import { RecaptchaFormsModule, RecaptchaModule, RecaptchaSettings, RECAPTCHA_SETTINGS } from 'ng-recaptcha-2';
import { BlockableDivComponent } from './components/blockable-div/blockable-div.component';
import { AgGridDynamicComponent } from './components/ag-grid-dynamic/grid-dynamic/ag-grid-dynamic.component';
import { GridHeaderComponent } from './components/ag-grid-dynamic/grid-header/grid-header.component';
import { FreeSearchComponent } from './components/free-search/free-search.component';
import { DelayEventDirective } from './directives/delay-event.directive';
import { NumberFloatingFilterComponent } from './components/ag-grid-dynamic/framework-components/filters/number-floating-filter.component';
import { EnumTranslateColumnComponent } from './components/ag-grid-dynamic/framework-components/renderers/enum-translate-column.component';
import { GridReloadDataDirective } from './directives/grid-reload-data.directive';
import { CanLeaveComponentGuard } from './guards/can-leave-component.guard';
import { ObservableFilterPipe } from './pipes/observable-filter.pipe';
import { PipeRendererComponent } from './components/ag-grid-dynamic/framework-components/renderers/pipe-renderer.component';
import { DynamicPipe } from './pipes/dynamic.pipe';
import { TemplateRendererComponent } from './components/ag-grid-dynamic/framework-components/renderers/template-renderer.component';
import { BtnCellRenderer } from './components/ag-grid-dynamic/framework-components/renderers/button-cell-renderer.component';
import { CustomDateFloatFilterComponent } from './components/ag-grid-dynamic/framework-components/filters/custom-date-float-filter.component';
import { DetailTesterComponent } from './components/detail-tester/detail-tester.component';
import { FirstColumnTemplateComponent } from './components/ag-grid-dynamic/framework-components/templates/first-column-template.component';
import { CheckBoxIconRenderer } from './components/ag-grid-dynamic/framework-components/renderers/checkbox-renderer.component';
import { GalleriaComponent } from './components/galleria/galleria.component';
import { ListSearchComponent } from './components/list-search/list-search.component';
import { FilterListPipe } from './pipes/filter-list.pipe';
import { RemoveEmptiesPipe } from './pipes/remove-empties.pipe';
import { FormControlErrorComponent } from './components/form-control-error/form-control-error.component';
import { ColumnHeaderTemplateComponent } from './components/ag-grid-dynamic/framework-components/templates/column-header-template.component';
import { MaxlengthMaskDirective } from './directives/input-masks/maxlength-mask.directive';
import { PhoneMaskDirective } from './directives/input-masks/phone-mask.directive';
import { PasswordComponent } from './components/password/password.component';
import { FormValidationsService } from './form-validations/services/form-validations.service';
import { DateMaskDirective } from './directives/input-masks/date-mask.directive';
import { MasterDetailsRendererComponent } from './components/ag-grid-dynamic/framework-components/renderers/master-details-renderer.component';
import { AppendTextPipe } from './pipes/append-text.pipe';
import { UploadImageComponent } from './components/upload-image/upload-image.component';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { SideBarOverrideService } from './external-modules-overrides/primeng-overrides/sidebar-override.service';
import { SplitterModule } from 'primeng/splitter';
import { ReadonlyTableComponent } from './components/table/readonly-table/readonly-table.component';
import { CopyToClipboardDirective } from './directives/copy-to-clipboard.directive';
import { BlockButtonToSingleClickDirective } from './directives/block-button-to-single-click ';
import { PagingComponent } from './components/paging/paging.component';

const recaptchaSettings: RecaptchaSettings = { siteKey: '6LeIb-4UAAAAAF1M_0wb0txuN530pjOy_ZM2uFpT' }


export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  imports: [CommonModule, ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    DynamicDialogModule,
    AgGridModule,//.withComponents([NumberFloatingFilterComponent, EnumTranslateColumnComponent, BtnCellRenderer, CustomDateFloatFilterComponent,CheckBoxIconRenderer]),
    RecaptchaModule, RecaptchaFormsModule, SidebarModule,
    ImageModule,
    CalendarModule,
    AccordionModule,
    AutoCompleteModule,
    AvatarModule,
    BreadcrumbModule,
    ButtonModule,
    CascadeSelectModule,
    CardModule,
    CarouselModule,
    CheckboxModule,
    ChipsModule,
    ConfirmDialogModule,
    ColorPickerModule,
    ContextMenuModule,
    DataViewModule,
    DialogModule,
    DividerModule,
    DropdownModule,
    FieldsetModule,
    FileUploadModule,
    GalleriaModule,
    InplaceModule,
    InputMaskModule,
    InputNumberModule,
    InputSwitchModule,
    InputTextModule,
    InputTextareaModule,
    ListboxModule,
    MegaMenuModule,
    MenuModule,
    MenubarModule,
    MessageModule,
    MessagesModule,
    MultiSelectModule,
    OrderListModule,
    OverlayPanelModule,
    SidebarModule,
    PaginatorModule,
    PanelModule,
    PanelMenuModule,
    PasswordModule,
    PickListModule,
    ProgressBarModule,
    RadioButtonModule,
    RatingModule,
    ScrollPanelModule,
    SelectButtonModule,
    SlideMenuModule,
    SliderModule,
    SpinnerModule,
    SplitButtonModule,
    StepsModule,
    TableModule,
    TabMenuModule,
    TabViewModule,
    TerminalModule,
    TieredMenuModule,
    ToastModule,
    ToggleButtonModule,
    ToolbarModule,
    TooltipModule,
    TreeModule,
    TreeTableModule,
    VirtualScrollerModule,
    BlockUIModule,
    ProgressSpinnerModule,
    TagModule,
    SplitterModule

  ],
  exports: [
    DynamicDialogModule,
    ReadonlyTableComponent,
    ImageModule,
    SidebarModule,
    GridReloadDataDirective,
    DelayEventDirective,
    CaptchaComponent, AgGridDynamicComponent, BlockableDivComponent,
    EnumTranslatePipe, MaxValidatorDirective, MinValidatorDirective, FilterPipe, AppendTextPipe,
    FormsModule, ReactiveFormsModule, TranslateModule,
    CalendarModule,
    AccordionModule,
    AutoCompleteModule,
    AvatarModule,
    BreadcrumbModule,
    ButtonModule,
    CascadeSelectModule,
    CardModule,
    CarouselModule,
    CheckboxModule,
    ChipsModule,
    ConfirmDialogModule,
    ColorPickerModule,
    ContextMenuModule,
    DataViewModule,
    DialogModule,
    DividerModule,
    DropdownModule,
    FieldsetModule,
    FileUploadModule,
    GalleriaComponent,
    GalleriaModule,
    InplaceModule,
    InputMaskModule,
    InputNumberModule,
    InputSwitchModule,
    InputTextModule,
    InputTextareaModule,
    ListboxModule,
    MegaMenuModule,
    MenuModule,
    MenubarModule,
    MultiSelectModule,
    OrderListModule,
    OverlayPanelModule,
    SidebarModule,
    PaginatorModule,
    PanelModule,
    PanelMenuModule,
    PasswordModule,
    PickListModule,
    ProgressBarModule,
    RadioButtonModule,
    RatingModule,
    ScrollPanelModule,
    SelectButtonModule,
    SlideMenuModule,
    SliderModule,
    SpinnerModule,
    SplitButtonModule,
    StepsModule,
    TableModule,
    TabMenuModule,
    TabViewModule,
    TerminalModule,
    TagModule,
    TieredMenuModule,
    ToastModule,
    ToggleButtonModule,
    ToolbarModule,
    TooltipModule,
    TreeModule,
    TreeTableModule,
    VirtualScrollerModule,
    ProvideParentForm,
    AppAutofocus,
    OnlyNumber,
    AppEqualToValueValidator,
    AppEqualValidator,
    AppNotZeroValidator,
    AppUrlValidator,
    EnumTranslatePipe,
    PasswordViewPipe,
    CalendarComponent,
    DelayClickDirective,
    UploadFileComponent,
    AmountInputComponent,
    BlockUIModule,
    ProgressSpinnerModule,
    CustomMessageComponent,
    DialogComponent,
    CodeVerificationComponent,
    TelOrMailComponent,
    TelOrMailDirective,
    ObservableFilterPipe,
    DynamicPipe,
    DetailTesterComponent,
    FilterListPipe,
    ListSearchComponent,
    PagingComponent,
    FormGroupComponent,
    FormControlDirective,
    FormValidationDirective,
    MessagesComponent,
    PasswordComponent,
    UploadImageComponent,
    SplitterModule,
    CopyToClipboardDirective,
    BlockButtonToSingleClickDirective
  ],
  declarations: [
    AppendTextPipe,
    BtnCellRenderer,
    MasterDetailsRendererComponent,
    CheckBoxIconRenderer,
    EnumTranslateColumnComponent,
    PipeRendererComponent,
    TemplateRendererComponent,
    CustomDateFloatFilterComponent,
    NumberFloatingFilterComponent, BlockableDivComponent, CaptchaComponent, AgGridDynamicComponent, EnumTranslatePipe, MaxValidatorDirective, MinValidatorDirective,
    ProvideParentForm,
    AppAutofocus,
    OnlyNumber,
    AppEqualToValueValidator,
    AppEqualValidator,
    AppNotZeroValidator,
    AppUrlValidator,
    EnumTranslatePipe,
    PasswordViewPipe,
    CalendarComponent,
    GalleriaComponent,
    GridReloadDataDirective,
    DelayEventDirective,
    DelayClickDirective,
    UploadFileComponent,
    CustomMessageComponent,
    FilterPipe,
    FilterListPipe,
    AmountInputComponent,
    DialogComponent,
    TelOrMailComponent,
    CodeVerificationComponent,
    TelOrMailDirective,
    GridHeaderComponent,
    FreeSearchComponent,
    ObservableFilterPipe,
    DynamicPipe,
    DetailTesterComponent,
    FirstColumnTemplateComponent,
    ColumnHeaderTemplateComponent,
    ListSearchComponent,
    RemoveEmptiesPipe,
    FormControlErrorComponent,
    MaxlengthMaskDirective,
    PhoneMaskDirective,
    DateMaskDirective,
    FormGroupComponent,
    FormControlDirective,
    FormValidationDirective,
    MessagesComponent,
    PasswordComponent,
    UploadImageComponent,
    ReadonlyTableComponent,
    CopyToClipboardDirective,
    BlockButtonToSingleClickDirective,
    PagingComponent
  ]
})
export class SharedModule {
  static forRootOrChild(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,

      // the singletone providers - one instance for all modules
      providers: [HttpClient,
        CanLeaveComponentGuard,
        AuthGuard,
        AppAuthService,//, AuthInterceptor, 
        CommonService,
        APIInterceptor,
        AdvMessageService, ConfirmationService,
        EnumService,
        DatePipe,
        PasswordViewPipe,
        FilterListPipe,
        FilterPipe,
        DecimalPipe,
        ObservableFilterPipe,
        DynamicPipe,
        FormValidationsService,
        { provide: RECAPTCHA_SETTINGS, useValue: recaptchaSettings },
        DialogService,
        SideBarOverrideService

      ]
    };
  }
}
